import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import {
  getStoredAudioEnabled,
  getStoredAudioVolume,
  getStoredAudioVariant,
  storeAudioEnabled,
  storeAudioVolume,
  storeAudioVariant,
  setAudioEnabled as syncEnabled,
  setAudioVolume as syncVolume,
  setAudioVariant as syncVariant,
  unlockAudio,
  playCue as playCueDirect,
} from '../utils/audio-theme';
import type { AudioCue, AudioVariant } from '../audio/registry';

interface AudioThemeContextValue {
  enabled: boolean;
  volume: number;
  variant: AudioVariant;
  setEnabled: (enabled: boolean) => void;
  setVolume: (volume: number) => void;
  setVariant: (variant: AudioVariant) => void;
  play: (cue: AudioCue) => void;
}

const AudioThemeContext = createContext<AudioThemeContextValue | null>(null);

export function AudioThemeProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabledState] = useState<boolean>(getStoredAudioEnabled);
  const [volume, setVolumeState] = useState<number>(getStoredAudioVolume);
  const [variant, setVariantState] = useState<AudioVariant>(getStoredAudioVariant);

  // Unlock the AudioContext on the first user gesture (browser autoplay
  // policy). One-shot — listeners remove themselves on first fire.
  useEffect(() => {
    const handler = () => {
      unlockAudio();
      window.removeEventListener('pointerdown', handler);
      window.removeEventListener('keydown', handler);
    };
    window.addEventListener('pointerdown', handler, { once: false });
    window.addEventListener('keydown', handler, { once: false });
    return () => {
      window.removeEventListener('pointerdown', handler);
      window.removeEventListener('keydown', handler);
    };
  }, []);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    storeAudioEnabled(next);
    syncEnabled(next);
  }, []);

  const setVolume = useCallback((next: number) => {
    const clamped = Math.min(1, Math.max(0, next));
    setVolumeState(clamped);
    storeAudioVolume(clamped);
    syncVolume(clamped);
  }, []);

  const setVariant = useCallback((next: AudioVariant) => {
    setVariantState(next);
    storeAudioVariant(next);
    syncVariant(next);
  }, []);

  const play = useCallback((cue: AudioCue) => {
    playCueDirect(cue);
  }, []);

  return (
    <AudioThemeContext.Provider
      value={{ enabled, volume, variant, setEnabled, setVolume, setVariant, play }}
    >
      {children}
    </AudioThemeContext.Provider>
  );
}

export function useAudioTheme(): AudioThemeContextValue {
  const ctx = useContext(AudioThemeContext);
  if (!ctx) {
    throw new Error('useAudioTheme must be used within an AudioThemeProvider');
  }
  return ctx;
}
